import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { GraphEdge, VertexPreview } from '../../types/graph';
import { getVertexUrl } from '../../services/vertex';
import { getApiEndpoint, vertexFetch } from '../../../constants.js';
import { fetchPreviewsBatch } from '../../services/api';
import { EdgeList } from './EdgeList';
import { VertexCard } from './VertexCard';
import { VertexAutocomplete } from './VertexAutocomplete';
import { route } from '../HashRouter';

export interface GraphUIProps {
  vertexUrl: string;
  trail?: string;  // Comma-separated encoded vertex URLs for breadcrumb trail
  onClose: () => void;
}

export function GraphUI({ vertexUrl, trail, onClose }: GraphUIProps) {
  // Parse breadcrumb trail
  const pathVertices = trail ? trail.split(',').map(decodeURIComponent) : [];
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [outgoingEdges, setOutgoingEdges] = useState<GraphEdge[]>([]);
  const [incomingEdges, setIncomingEdges] = useState<GraphEdge[]>([]);
  const [previews, setPreviews] = useState<Record<string, VertexPreview>>({});
  const [showSourceAutocomplete, setShowSourceAutocomplete] = useState(false);
  const [showTargetAutocomplete, setShowTargetAutocomplete] = useState(false);

  // Get display title for a URL from previews, fallback to domain
  const getDisplayTitle = (url: string): string => {
    const preview = previews[url];
    if (preview?.title) return preview.title;
    try {
      return new URL(url).hostname;
    } catch (_) {
      return url;
    }
  };

  // Navigate to a breadcrumb item. Set `from` to the current URL so Back
  // from the destination returns here (and Back from THERE follows the
  // chain further, since the destination's `from` embeds our own).
  const navigateToBreadcrumb = (targetUrl: string, index: number) => {
    const newTrail = pathVertices.slice(0, index).map(encodeURIComponent).join(',');
    const encodedTarget = encodeURIComponent(targetUrl);
    const encodedFrom = encodeURIComponent(window.location.hash.slice(1));
    const trailParam = newTrail ? `trail=${newTrail}&` : '';
    route(`/graph/${encodedTarget}?${trailParam}from=${encodedFrom}`);
  };

  useEffect(() => {
    loadGraphData();
  }, [vertexUrl]);

  const loadGraphData = async () => {
    setLoading(true);
    setError(null);

    try {
      const apiEndpoint = await getApiEndpoint();
      const encodedUrl = encodeURIComponent(vertexUrl);

      // Fetch edges using cookie-based auth
      const [outgoingRes, incomingRes] = await Promise.all([
        vertexFetch(`${apiEndpoint}/user/vertex/graph/source/edges?vertex=${encodedUrl}`, {
          credentials: 'include'
        }),
        vertexFetch(`${apiEndpoint}/user/vertex/graph/target/edges?vertex=${encodedUrl}`, {
          credentials: 'include'
        })
      ]);

      if (!outgoingRes.ok || !incomingRes.ok) {
        throw new Error('Failed to fetch edges');
      }

      const [outgoing, incoming] = await Promise.all([
        outgoingRes.json() as Promise<GraphEdge[]>,
        incomingRes.json() as Promise<GraphEdge[]>
      ]);

      console.log('[GraphUI] Fetched edges:', { outgoing, incoming });

      setOutgoingEdges(outgoing);
      setIncomingEdges(incoming);

      // Collect all vertex URLs that need previews
      const vertexUrls = new Set<string>();
      vertexUrls.add(vertexUrl); // Current vertex

      // Add path vertices for breadcrumb titles
      pathVertices.forEach(url => vertexUrls.add(url));

      outgoing.forEach(edge => {
        const url = getVertexUrl(edge.target);
        if (url) vertexUrls.add(url);
      });

      incoming.forEach(edge => {
        const url = getVertexUrl(edge.source);
        if (url) vertexUrls.add(url);
      });

      console.log('[GraphUI] Vertex URLs to fetch previews for:', Array.from(vertexUrls));

      // Fetch previews in batch using centralized API
      try {
        const previewMap = await fetchPreviewsBatch(Array.from(vertexUrls));
        console.log('[GraphUI] Preview map:', previewMap);
        setPreviews(previewMap as Record<string, VertexPreview>);
      } catch (err) {
        console.error('[GraphUI] Failed to fetch previews:', err);
      }

    } catch (err) {
      console.error('[GraphUI] Error loading graph:', err);
      setError(err instanceof Error ? err.message : 'Failed to load graph');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSource = async (sourceUrl: string, info: string | null) => {
    try {
      const apiEndpoint = await getApiEndpoint();

      const body: any = {
        source: sourceUrl,
        target: vertexUrl
      };
      if (info) {
        body.info = info;
      }

      const response = await vertexFetch(`${apiEndpoint}/user/vertex/graph/edge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error(`Failed to create edge: ${response.status}`);
      }

      setShowSourceAutocomplete(false);
      await loadGraphData();
    } catch (err) {
      console.error('[GraphUI] Error adding source:', err);
      alert('Failed to add source: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const handleAddTarget = async (targetUrl: string, info: string | null) => {
    try {
      const apiEndpoint = await getApiEndpoint();

      const body: any = {
        source: vertexUrl,
        target: targetUrl
      };
      if (info) {
        body.info = info;
      }

      const response = await vertexFetch(`${apiEndpoint}/user/vertex/graph/edge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error(`Failed to create edge: ${response.status}`);
      }

      setShowTargetAutocomplete(false);
      await loadGraphData();
    } catch (err) {
      console.error('[GraphUI] Error adding target:', err);
      alert('Failed to add target: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const handleDeleteEdge = async (edge: GraphEdge) => {
    if (!confirm('Delete this reference?')) return;

    try {
      const apiEndpoint = await getApiEndpoint();

      const sourceVertex = encodeURIComponent(edge.source.rendered);
      const targetVertex = encodeURIComponent(edge.target.rendered);

      const response = await vertexFetch(
        `${apiEndpoint}/user/vertex/graph/edge?sourceVertex=${sourceVertex}&targetVertex=${targetVertex}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete edge: ${response.status}`);
      }

      // Reload graph data
      await loadGraphData();

    } catch (err) {
      console.error('[GraphUI] Error deleting edge:', err);
      alert('Failed to delete reference: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const handleUpdateEdgeInfo = async (edge: GraphEdge, info: string | null) => {
    try {
      const apiEndpoint = await getApiEndpoint();

      const response = await vertexFetch(`${apiEndpoint}/user/vertex/graph/edge`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          source: edge.source.rendered,
          target: edge.target.rendered,
          info: info
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update edge: ${response.status}`);
      }

      await loadGraphData();
    } catch (err) {
      console.error('[GraphUI] Error updating edge:', err);
      alert('Failed to update note: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  // Simple breadcrumb for loading/error states (no previews yet)
  const renderSimpleBreadcrumbs = () => (
    <nav class="graph-breadcrumbs">
      <a class="breadcrumb-link" onClick={onClose}>List</a>
      {pathVertices.map((url, index) => {
        try {
          const hostname = new URL(url).hostname;
          return (
            <>
              <span class="breadcrumb-separator">›</span>
              <a class="breadcrumb-link" onClick={() => navigateToBreadcrumb(url, index)}>
                {hostname}
              </a>
            </>
          );
        } catch (_) {
          return null;
        }
      })}
      <span class="breadcrumb-separator">›</span>
      <span class="breadcrumb-current">Loading...</span>
    </nav>
  );

  const renderBackButton = () => (
    <button class="btn-back" onClick={onClose}>← Back</button>
  );

  if (loading) {
    return (
      <div class="graph-view">
        <div class="graph-view-header">
          {renderBackButton()}
          {renderSimpleBreadcrumbs()}
        </div>
        <div class="loading">Loading graph data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div class="graph-view">
        <div class="graph-view-header">
          {renderBackButton()}
          {renderSimpleBreadcrumbs()}
        </div>
        <div class="error-message">{error}</div>
      </div>
    );
  }

  // Build breadcrumb items
  const renderBreadcrumbs = () => (
    <nav class="graph-breadcrumbs">
      <a class="breadcrumb-link" onClick={onClose}>List</a>
      {pathVertices.map((url, index) => (
        <>
          <span class="breadcrumb-separator">›</span>
          <a class="breadcrumb-link" onClick={() => navigateToBreadcrumb(url, index)}>
            {getDisplayTitle(url)}
          </a>
        </>
      ))}
      <span class="breadcrumb-separator">›</span>
      <span class="breadcrumb-current">{getDisplayTitle(vertexUrl)}</span>
    </nav>
  );

  return (
    <div class="graph-view">
      <div class="graph-view-header">
        {renderBackButton()}
        {renderBreadcrumbs()}
      </div>

      <div class="graph-content">
        {/* Row 1: Incoming edges (sources that reference V) */}
        <div class="graph-section">
          <div class="graph-section-header">
            <button
              class="btn-add-edge"
              onClick={() => setShowSourceAutocomplete(true)}
              title="Add source reference"
            >+</button>
            <span class="graph-section-count">{incomingEdges.length}</span>
            <span class="graph-section-label">Sources → V</span>
          </div>
          {showSourceAutocomplete && (
            <VertexAutocomplete
              onSelect={handleAddSource}
              onCancel={() => setShowSourceAutocomplete(false)}
              excludeUrl={vertexUrl}
            />
          )}
          <EdgeList
            edges={incomingEdges}
            previews={previews}
            direction="incoming"
            currentVertexUrl={vertexUrl}
            currentTrail={trail}
            onDelete={handleDeleteEdge}
            onUpdateInfo={handleUpdateEdgeInfo}
          />
        </div>

        {/* Row 2: Current vertex V */}
        <VertexCard url={vertexUrl} preview={previews[vertexUrl]} />

        {/* Row 3: Outgoing edges (targets that V references) */}
        <div class="graph-section">
          <div class="graph-section-header">
            <span class="graph-section-label">V →</span>
            <span class="graph-section-count">{outgoingEdges.length}</span>
            <span class="graph-section-label">Targets</span>
            <button
              class="btn-add-edge"
              onClick={() => setShowTargetAutocomplete(true)}
              title="Add target reference"
            >+</button>
          </div>
          {showTargetAutocomplete && (
            <VertexAutocomplete
              onSelect={handleAddTarget}
              onCancel={() => setShowTargetAutocomplete(false)}
              excludeUrl={vertexUrl}
            />
          )}
          <EdgeList
            edges={outgoingEdges}
            previews={previews}
            direction="outgoing"
            currentVertexUrl={vertexUrl}
            currentTrail={trail}
            onDelete={handleDeleteEdge}
            onUpdateInfo={handleUpdateEdgeInfo}
          />
        </div>
      </div>
    </div>
  );
}
